import React, { useEffect, useState } from 'react';
import { X, Trash2, Plus, Star } from 'lucide-react';
import EmployeeAvatar from './EmployeeAvatar';
import { Button } from './ui/button';
import { useT } from '../contexts/LocaleProvider';
import api from '../utils/api';

const fmtIban = (iban) => (iban || '').replace(/(.{4})/g, '$1 ').trim();

/**
 * Self-service "Mon profil" modal · opened from the user dropdown.
 * Two sections live here today · the portrait that propagates across
 * every EmployeeAvatar in the hub, and the bank accounts used by the
 * expense submission flow. Anything that requires admin curation (job
 * title, department, manager) stays in the HR admin tab.
 */
export default function ProfilePhotoModal({ open, onClose, onUpdated }) {
  const t = useT();
  const [emp, setEmp] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    api.myEmployee().then(setEmp).catch(() => setEmp(null));
    api.myBankAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, [open]);

  if (!open) return null;

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError('');
    try {
      const updated = await api.uploadMyPhoto(file);
      setEmp(updated);
      onUpdated?.(updated);
    } catch (err) {
      setError(err.response?.data?.detail || t('profile.photo.failed') || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const clearPhoto = async () => {
    if (!emp?.photo) return;
    setBusy(true); setError('');
    try {
      const updated = await api.clearMyPhoto();
      setEmp(updated);
      onUpdated?.(updated);
    } catch (err) {
      setError(err.response?.data?.detail || t('profile.photo.failed') || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto"
         style={{ background: 'rgba(20, 30, 25, 0.4)' }}
         onClick={onClose}>
      <div className="w-full max-w-[480px] rounded-2xl bg-[hsl(var(--surface))] border border-border shadow-xl my-auto"
           onClick={(e) => e.stopPropagation()}
           role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="font-display text-[1.05rem]">{t('profile.title') || 'Mon profil'}</h2>
          <button type="button" onClick={onClose}
                  className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                  aria-label={t('common.close')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Photo section */}
        <section className="p-5 border-b border-border">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-3 font-medium">
            {t('profile.photo.section') || 'Photo'}
          </div>
          <div className="flex items-center gap-5">
            <EmployeeAvatar
              fullName={emp?.full_name}
              email={emp?.email}
              photo={emp?.photo}
              size={96}
            />
            <div className="flex-1 space-y-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-border rounded-md cursor-pointer hover:bg-[hsl(var(--surface-2))]">
                <input type="file" accept="image/*" onChange={pickPhoto} disabled={busy} className="hidden" />
                {busy ? t('common.saving') : (emp?.photo ? (t('profile.photo.replace') || 'Remplacer') : (t('profile.photo.upload') || 'Téléverser'))}
              </label>
              {emp?.photo && (
                <button type="button" onClick={clearPhoto} disabled={busy}
                        className="inline-flex items-center gap-1 text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]">
                  <Trash2 className="h-3 w-3" /> {t('common.delete')}
                </button>
              )}
            </div>
          </div>
          <p className="text-[11.5px] text-[hsl(var(--text-muted))] mt-3">
            {t('profile.photo.hint') || 'PNG / JPG · visible partout dans le hub.'}
          </p>
        </section>

        {/* Bank accounts section */}
        <BankAccountsSection
          t={t}
          accounts={accounts}
          setAccounts={setAccounts}
          setError={setError}
        />

        {error && (
          <div className="px-5 py-2 text-[12px] text-[hsl(var(--danger))]">{error}</div>
        )}

        <div className="px-5 py-3 border-t border-border flex justify-end">
          <Button size="sm" onClick={onClose}>{t('common.close')}</Button>
        </div>
      </div>
    </div>
  );
}

const BankAccountsSection = ({ t, accounts, setAccounts, setError }) => {
  const [iban, setIban] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!iban.trim()) return;
    setBusy(true); setError('');
    try {
      const created = await api.createBankAccount({ iban: iban.trim(), label: label.trim() });
      setAccounts(arr => [...arr, created]);
      setIban(''); setLabel('');
    } catch (e) {
      const d = e.response?.data;
      setError(Array.isArray(d?.iban) ? d.iban[0] : (d?.detail || t('bank.saveFailed') || 'Failed'));
    } finally { setBusy(false); }
  };
  const makePref = async (id) => {
    try {
      await api.updateBankAccount(id, { is_preferred: true });
      setAccounts(arr => arr.map(a => ({ ...a, is_preferred: a.id === id })));
    } catch (e) {
      setError(e.response?.data?.detail || t('bank.saveFailed') || 'Failed');
    }
  };
  const remove = async (id) => {
    if (!window.confirm(t('bank.confirmDelete') || t('common.confirmDelete'))) return;
    try {
      await api.deleteBankAccount(id);
      setAccounts(arr => arr.filter(a => a.id !== id));
    } catch (e) {
      setError(e.response?.data?.detail || t('bank.saveFailed') || 'Failed');
    }
  };

  return (
    <section className="p-5 border-b border-border">
      <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-3 font-medium">
        {t('admin.bankAccounts') || 'Comptes bancaires'}
      </div>
      {accounts.length === 0 && (
        <p className="text-[13px] text-[hsl(var(--text-soft))] mb-3">{t('bank.empty') || 'Aucun compte bancaire.'}</p>
      )}
      <ul className="space-y-1.5 mb-3">
        {accounts.map(a => (
          <li key={a.id} className="flex items-center gap-3 px-3 py-2 border border-border rounded-md bg-[hsl(var(--bg-subtle))] text-[13px]">
            <span className="font-num flex-1 truncate">
              {fmtIban(a.iban)}{a.label ? ` · ${a.label}` : ''}
            </span>
            {a.is_preferred
              ? <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--accent))]">
                  <Star className="h-3 w-3 fill-current" /> {t('bank.preferred') || 'Préféré'}
                </span>
              : <button type="button" onClick={() => makePref(a.id)}
                        className="text-[11px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">
                  {t('bank.makePreferred') || 'Définir préféré'}
                </button>}
            <button type="button" onClick={() => remove(a.id)}
                    className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]"
                    aria-label={t('common.delete')}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-end gap-2">
        <input className="input !w-56 font-num text-[13px]" placeholder="BE71 0961 2345 6769"
               value={iban} onChange={e => setIban(e.target.value)} />
        <input className="input !w-32 text-[13px]" placeholder={t('bank.label.ph') || 'Libellé'}
               value={label} onChange={e => setLabel(e.target.value)} />
        <Button size="sm" onClick={add} disabled={busy || !iban.trim()} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" /> {t('bank.add') || 'Ajouter'}
        </Button>
      </div>
    </section>
  );
};
